import { nonnull } from "@tento-core/assert";
import type { Option } from "@tento-lona/sheets";
import { Month, Month1, NaiveDate, PartialDate, Weekday } from "@tento-chrono";
import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Style } from "@tento-ui/component-styles";
import { SpanRangeCalculator } from "./span-range.ts";
import { SlotCard } from "@tento-ui/components/slot/slot-card";
import { DomUtils } from "@tento-ui/dom";
import type { DateFormat } from "@tento-lona";

/**
 * Reuse `$cell`'s sole child when it is already an instance of the wanted
 * component class; otherwise clear the cell and mint one. Reconnecting an
 * existing child makes Blink rebuild its shadow-scope style resolver (a
 * whole-subtree recalc), so for header cells that rebind on every column
 * crossing, reuse-in-place is the difference between one subtree recalc per
 * bind and none.
 */
export function reuseHeaderChild<T extends typeof LonaWebComponent>(
  $cell: HTMLElement,
  cls: T,
): InstanceType<T> {
  const $existing = $cell.firstElementChild;
  if ($existing instanceof cls && $cell.children.length === 1) {
    return $existing as InstanceType<T>;
  }
  DomUtils.clearChildren($cell);
  const $fresh = cls.make<T>();
  $cell.appendChild($fresh);
  return $fresh;
}

@component({
  name: "std-column-header",
})
export class ColumnHeader extends LonaWebComponent {
  /**
   * Bind the header of `column`; `period` marks the column holding today
   * (`"current"`) apart from every other.
   */
  bind(
    column: NaiveDate.Partial.WithTz,
    period: "current" | "other",
    onClickHeader: Option<(column: NaiveDate.Partial.WithTz) => void>,
    dateFormat: DateFormat = "month-day",
  ) {
    const isCurrent = period === "current";
    this.toggleAttribute("current", isCurrent);

    // Set ARIA columnheader role and accessible label
    this.setAttribute("role", "columnheader");

    // Keep the card mounted: reconnecting it rebuilds its shadow styles
    // during every column resize, even when its labels have not changed.
    const $card = reuseHeaderChild(this, SlotCard);
    $card.toggleAttribute("centered", column[0].type === "day");
    GESTURE_MANAGER.addPointerEvent($card, {
      onClick: () => {
        $card.flash();
        onClickHeader?.(column);
      },
    });
    $card.assignStyles({
      paddingInline: "1px",
      paddingTop: "3px",
    });

    const label = ColumnHeader.label(column[0], dateFormat);
    $card.bind(label.value, label.caption, isCurrent);
    this.setAttribute("aria-label", label.aria);
    this.setAttribute("date-format", dateFormat);
  }

  static $styles: Style.Compat[] = [];
  static $html: HTMLTemplateElement = html`
    <std-col id=root>
      <slot></slot>
    </std-col>
  `;
}

export namespace ColumnHeader {
  /** What a header card reads for one column: its value, caption and accessible label. */
  export type Label = { readonly value: string; readonly caption: string; readonly aria: string };

  /**
   * The card text of the column at `date` — a pure function of its range and
   * the date format, so a header placed for a range reads its real text
   * before (and without) its bind: day number and weekday, the range and
   * "week N", the range and the month name, or the year.
   */
  export function label(date: PartialDate, dateFormat: DateFormat): Label {
    const resolved = date.toRange();
    const start = resolved.start;
    const end = resolved.end.addDays(-1);
    const slash = (d: NaiveDate) => dateFormat === "day-month" ? `${d.day}/${d.month.mth1}` : `${d.month.mth1}/${d.day}`;
    const range = start.equals(end) ? slash(start) : `${slash(start)} \u2013 ${slash(end)}`;
    switch (date.type) {
      case "year":
        return { value: "Year", caption: String(date.yr), aria: `Year ${date.yr}` };
      case "month": {
        const monthName = nonnull(Month.MONTHS_OF_YEAR[nonnull(date.mth, "a week column has its month") - 1], "a month has a name");
        return { value: range, caption: monthName, aria: `${monthName} ${date.yr}` };
      }
      case "day":
        return {
          value: String(start.day),
          caption: start.dayOfWeek.shortname,
          aria: NaiveDate.Formatter.render(start, { dateFormat, includeWeekday: true, includeYear: true }),
        };
      case "week":
        return { value: range, caption: "week " + String(start.isoWno), aria: `Week ${start.isoWno}, ${start.yr}` };
      default:
        return { value: "", caption: "", aria: date.toString() };
    }
  }

  /** The date format a header column's card was last bound with ("month-day" before any bind). */
  export function boundDateFormat($header: Element): DateFormat {
    return $header.querySelector("std-column-header")?.getAttribute("date-format") === "day-month" ? "day-month" : "month-day";
  }

  export function getWeekStartsInMonth(month: PartialDate, weekStart: Weekday): NaiveDate[] {
    const start = month.start;
    const end = month.end;

    // Find the first weekStart day on or after month start
    let first = start;
    while (first.dayOfWeek !== weekStart) {
      first = first.addDays(1);
    }

    const dates: NaiveDate[] = [];
    let current = first;
    while (current.isBefore(end)) {
      dates.push(current);
      current = current.addDays(7);
    }
    return dates;
  }

  /**
   * Gets the months within a year as PartialDate ranges.
   * Returns all 12 months for a full year.
   */
  export function getMonthsInYear(year: PartialDate): PartialDate[] {
    const yr = year.yr;
    const months: PartialDate[] = [];
    for (let m = 1; m <= 12; m++) {
      months.push(new NaiveDate.Partial("month", yr, m as Month1));
    }
    return months;
  }

  /**
   * Gets the quarters within a year as NaiveDate.Range spans.
   * Returns 4 quarters (Q1: Jan-Mar, Q2: Apr-Jun, Q3: Jul-Sep, Q4: Oct-Dec).
   */
  export function getQuartersInYear(year: PartialDate): NaiveDate.Range[] {
    const yr = year.yr;
    const quarters: NaiveDate.Range[] = [];
    for (let q = 0; q < 4; q++) {
      const startMonth = (q * 3 + 1) as Month1;
      const endMonth = (q * 3 + 4) as Month1;
      const start = new NaiveDate.Partial("month", yr, startMonth).start;
      const end =
        endMonth <= 12
          ? new NaiveDate.Partial("month", yr, endMonth).start
          : new NaiveDate.Partial("month", yr + 1, 1 as Month1).start;
      quarters.push(new NaiveDate.Range(start, end));
    }
    return quarters;
  }

  export interface SpanLabelInfo {
    label: string;
    sublabel: string;
  }

  export type SpanLabelFormatter = (span: NaiveDate.Range) => SpanLabelInfo;

  /**
   * Default formatter for week spans - shows day number and weekday.
   */
  export const weekSpanFormatter: SpanLabelFormatter = (span): SpanLabelInfo => ({
    label: String(span.start.day),
    // sublabel: `${span.start.dayOfWeek.shortname}, W${span.start.isoWno}`,
    sublabel: `W${span.start.isoWno}`,
  });

  /**
   * Formatter for month spans - shows month short name.
   */
  export const monthSpanFormatter: SpanLabelFormatter = (span): SpanLabelInfo => ({
    label: nonnull(Month.MONTHS_OF_YEAR_SHORT[span.start.month1 - 1], "a month has a short name"),
    sublabel: "",
  });

  export const monthAbbrFormatter: SpanLabelFormatter = (span): SpanLabelInfo => ({
    label: nonnull(Month.MONTHS_OF_YEAR_SHORT[span.start.month1 - 1], "a month has a short name").slice(0, 2),
    sublabel: "",
  });

  /**
   * Formatter for quarter spans - shows Q1, Q2, Q3, Q4.
   */
  export const quarterSpanFormatter: SpanLabelFormatter = (span): SpanLabelInfo => {
    const quarter = Math.floor((span.start.month1 - 1) / 3) + 1;
    return {
      label: `Q${quarter}`,
      sublabel: "",
    };
  };

  @component({
    name: "std-timeline-legend",
  })
  export class TimelineLegend extends LonaWebComponent {
    #onSelectSpan: Option<(span: NaiveDate.Range) => void> = null;

    bind(
      anchorDate: NaiveDate,
      spans: NaiveDate.Range[],
      spanRange: SpanRangeCalculator,
      now: NaiveDate,
      labelFormatter: SpanLabelFormatter,
      onSelectSpan: Option<(span: NaiveDate.Range) => void>,
    ) {
      this.#onSelectSpan = onSelectSpan;

      // Rebind existing SlotCards in place instead of clearChildren + fresh
      // makes: every reconnect rebuilds the card's shadow-scope style
      // resolver, and headers rebind per column crossing during scroll.
      for (
        let last = this.lastElementChild;
        last != null && this.children.length > spans.length;
        last = this.lastElementChild
      ) {
        last.remove();
      }

      let i = 0;
      for (const span of spans) {
        const { offset, width } = spanRange(anchorDate, span);
        const contains = span.contains(now);
        const { label, sublabel } = labelFormatter(span);

        const $existing = this.children[i] as Option<Element>;
        let $date = $existing instanceof SlotCard ? $existing : null;
        if ($date == null) {
          const $fresh = SlotCard.make();
          if ($existing) $existing.replaceWith($fresh);
          else this.appendChild($fresh);
          $date = $fresh;
        }
        $date.bind(label, sublabel, contains);
        $date.assignStyles({
          position: "absolute",
          left: `${offset - 0}px`,
          width: `${width - 2}px`,
          marginTop: "0px",
          marginInline: "1px",
        });
        $date.toggleAttribute("centered", true);
        $date.toggleAttribute("small", true);

        const $card = $date;
        GESTURE_MANAGER.addPointerEvent($card, {
          onClick: () => {
            $card.flash();
            this.#onSelectSpan?.(span);
          },
        });
        i++;
      }
    }

    static $styles: Style.Compat[] = [];
    static $html: Option<HTMLTemplateElement> = html`
      <std-col id="root" style="position: relative; width: 100%;">
        <slot></slot>
      </std-col>
    `;
  }
}
